import { formatPersonName } from '@/utils/personName';
/**
 * custInqToRaoPerson — map a ParsedCustInq (jXRL CustInq result) into the
 * RAO Person shape that IndividualPersonDialog consumes.
 *
 * Built for the CIF-maintenance unification (RCM reuses the same Personal
 * dialog surface the New Acct flow uses). The inverse `raoPersonToCustMod`
 * folds the officer's edits back into a CustModRequest that the shared
 * `/accounts/cust-mod` Function route already speaks — builds a diff against
 * the original ParsedCustInq so we only send changed fields.
 *
 * CIF-only extras that don't exist on RaoPerson (ID #1 + ID #2 blocks) ride
 * on `person.extra` — keys match the ones read by
 * `IndividualCipScanSection` (`idType`, `idNumber`, `idIssuer`,
 * `idIssueDate`, `idExpiryDate`, plus the 2nd-ID pair).
 */

import type { CustModRequest } from '@retail-ops/shared-types';
import type { CustomerType } from '@retail-ops/shared-types/enums';
import type { ParsedCustInq } from '@/utils/custInqParser';
import type {
  RaoPerson,
  RaoCitizenshipStatus,
  RaoGender,
  RaoTinCode,
} from '@/modules/RAO/types';

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function coerceCustomerType(raw: string): CustomerType | 'Minor' | undefined {
  if (!raw) return undefined;
  // Personal-group mapping: jXRL 'I' = Individual; pass-through for the other
  // canonical values the parser might return (CustType is usually the
  // long-form name for businesses).
  if (raw === 'I' || raw === 'Individual') return 'Individual';
  // Anything else — let TS guard at the type boundary; unknown strings are
  // dropped rather than coerced so the derivation engine can re-derive.
  const known: ReadonlyArray<CustomerType> = [
    'Individual', 'Corporation', 'LLC', 'DBA', 'SoleProp', 'Partnership',
    'PublicFund', 'NonProfit', 'Trust', 'Estate', 'Conservatorship', 'IRA',
    'UGMA', 'UTMA', 'LLP', 'LLLP', 'LimitedPartnership', 'GeneralPartnership',
    'Church', 'Association', 'Government', 'SchoolDistrict', 'MinorAccount',
    'Other',
  ];
  if ((known as ReadonlyArray<string>).includes(raw)) {
    return raw as CustomerType;
  }
  return undefined;
}

function coerceGender(g: string): RaoGender {
  if (g === 'Male' || g === 'Female') return g;
  return '';
}

function coerceCitizenship(): RaoCitizenshipStatus {
  // BACKLOG-0512 — default existing CIFs to 'US Citizen' so the modal-level
  // `local` state is the single source of truth and downstream consumers
  // (PdwStatusBadges, CitizenshipPicker, Origin tab indicator, gates) all
  // read a populated value. ParsedCustInq doesn't surface citizenship fields
  // yet, but PCB's CIF population is ~99% US citizens; officer can override
  // via the dialog and the diff still promotes the change to CustMod.
  // CitizenshipCountry when they save.
  return 'US Citizen';
}

function coerceTinCode(code: string): RaoTinCode | undefined {
  if (code === 'S' || code === 'E' || code === 'N') return code;
  return undefined;
}

// -----------------------------------------------------------------------------
// ParsedCustInq → RaoPerson
// -----------------------------------------------------------------------------

export function custInqToRaoPerson(inq: ParsedCustInq): RaoPerson {
  const fullName =
    inq.comName ||
    formatPersonName({ firstName: inq.firstName, middleName: inq.middleName, lastName: inq.lastName, suffix: inq.suffix }) ||
    '';

  const person: RaoPerson = {
    role: 'primary',
    mode: 'existing',

    firstName: inq.firstName,
    ...(inq.middleName ? { middleName: inq.middleName } : {}),
    lastName: inq.lastName,
    ...(inq.suffix ? { suffix: inq.suffix } : {}),
    fullName,
    ...(inq.comName ? { name2: inq.comName } : {}),
    cifId: inq.cifId,
    ...(inq.dob ? { dob: inq.dob } : {}),
    ...(inq.taxId ? { taxId: inq.taxId } : {}),
    ...(coerceTinCode(inq.tinCode) ? { tinCode: coerceTinCode(inq.tinCode)! } : {}),
    ...(coerceCustomerType(inq.customerType)
      ? { customerType: coerceCustomerType(inq.customerType)! }
      : {}),
    ...(inq.gender ? { gender: coerceGender(inq.gender) } : {}),
    ...(inq.insiderCode ? { insiderCode: inq.insiderCode } : {}),

    ...(inq.phoneHome ? { phoneHome: inq.phoneHome } : {}),
    ...(inq.phoneCell ? { phoneCell: inq.phoneCell } : {}),
    ...(inq.phoneBus ? { phoneBus: inq.phoneBus } : {}),
    ...(inq.email ? { email: inq.email } : {}),

    ...(inq.addr ? { addr: inq.addr } : {}),
    ...(inq.city ? { city: inq.city } : {}),
    ...(inq.state ? { state: inq.state } : {}),
    ...(inq.zip ? { zip: inq.zip } : {}),

    ...(inq.employer ? { employer: inq.employer } : {}),
    ...(inq.occupation ? { occupation: inq.occupation } : {}),
    ...(inq.naicsCode ? { naicsCode: inq.naicsCode } : {}),
    // Bill 2026-04-26: existing CIFs were rendering with empty employment
    // dropdowns because the parser wasn't extracting EmplStat. Now hydrated.
    ...(inq.employmentStatus ? { employmentStatus: inq.employmentStatus } : {}),

    // ParsedCustInq doesn't surface citizenship fields today — default blank
    // and let the officer override via the dialog.
    citizenshipStatus: coerceCitizenship(),

    extra: {
      // Provenance — this person came straight out of a Core Director CustInq,
      // so isExistingCustomer() must say so. Without it the CIP gate treated an
      // Edit-CIF load as a fresh add and demanded an ID capture even though the
      // ID below is on file and unexpired (Bill 2026-07-15).
      cifSourceMode: 'core-director-cif-load',
      // CIP — IndividualCipScanSection reads these off `extra`.
      idType: inq.idTypeCode,
      idNumber: inq.idNumber,
      idIssuer: inq.idState,
      idIssueDate: inq.idIssueDate,
      idExpiryDate: inq.idExpiryDate,
      // 2nd ID — PCB CUSTOM UDFs (F814/801/813/815/816), read back via
      // x_BusDetail. Populated only when the CIF was fetched with
      // ?includeUdf=true; blank otherwise, which is also the normal
      // "no 2nd ID on file" state. IndividualCipScanSection reads these keys.
      idType2: inq.idTypeCode2,
      idNumber2: inq.idNumber2,
      idIssuer2: inq.idIssuer2,
      idIssueDate2: inq.idIssueDate2,
      idExpiryDate2: inq.idExpiryDate2,
      // NetTeller id + tinCode echoed for completeness.
      existingNetTellerId: inq.existingNetTellerId,
    },
  };

  return person;
}

// -----------------------------------------------------------------------------
// RaoPerson → CustModRequest (diff vs. original ParsedCustInq)
// -----------------------------------------------------------------------------

export interface RaoPersonToCustModOptions {
  /**
   * REASSIGN the CIF's officer (Core Director Field 14) to this code.
   *
   * Pass this ONLY when deliberately changing the relationship owner. Editing a
   * customer's address does not make the editor their officer — Bill 2026-07-15:
   * "just because an operator is editing the CIF, it should not automatically
   * change the Officer Code to the editing operator's initials."
   *
   * Omitted ⇒ OffCode is left off the request ⇒ the server skips Field 14
   * entirely and the existing officer survives. That omission is the ONLY way to
   * preserve it: CustInq does not return Field 14 (probed 2026-07-15 — 120 leaf
   * paths, no OffCode), so it cannot be read and echoed back.
   *
   * History: this was effectively required and defaulted to '000' "to match
   * CifEditPanel" — doubly wrong. The legacy panel used
   * `userPermissions.officerCode` (CifUpdateScreen.tsx:313), and '000' silently
   * rewrote real officers (Bill's repro: Field 14 WW -> 000).
   *
   * "Who is editing" is {@link operatorNumber}, not this.
   */
  reassignOfficerCode?: string;
  /**
   * The Core Director operator making the change → jXchange `auditUsrId` → CD
   * Account History's "Operator" column. Omitted ⇒ that column reads "0 - System".
   */
  operatorNumber?: string;
  /** AuthMethod forwarded to Core Director. Defaults to 'InPerson'. */
  authMethod?: CustModRequest['AuthMethod'];
}

/**
 * Build a CustModRequest from the officer's edits. Only changed fields are
 * emitted (parity with the legacy SPFx CustMod payload).
 *
 * `cifId` is pulled from `originalInq.cifId` — we never trust the working
 * copy to rename a CIF in flight.
 */
export function raoPersonToCustMod(
  person: RaoPerson,
  originalInq: ParsedCustInq,
  options: RaoPersonToCustModOptions = {},
): CustModRequest {
  const req: CustModRequest = {
    CifId: originalInq.cifId,
    AuthMethod: options.authMethod ?? 'InPerson',
    // OffCode is NOT set here. Field 14 is the CIF's assigned officer, not a
    // record of who edited — leave it alone unless the caller explicitly asks to
    // reassign. The server only writes Field 14 when OffCode is non-empty, so
    // omission is the preserve-it path.
    ...(options.reassignOfficerCode ? { OffCode: options.reassignOfficerCode } : {}),
    // The editor's identity belongs HERE (→ auditUsrId → CD's Operator column).
    ...(options.operatorNumber ? { OperatorNumber: options.operatorNumber } : {}),
  };

  // Demographics
  if ((person.firstName ?? '') !== (originalInq.firstName ?? '')) {
    req.FirstName = person.firstName ?? '';
  }
  if ((person.middleName ?? '') !== (originalInq.middleName ?? '')) {
    req.MiddleName = person.middleName ?? '';
  }
  if ((person.lastName ?? '') !== (originalInq.lastName ?? '')) {
    req.LastName = person.lastName ?? '';
  }
  if ((person.suffix ?? '') !== (originalInq.suffix ?? '')) {
    req.Suffix = person.suffix ?? '';
  }
  if ((person.dob ?? '') !== (originalInq.dob ?? '')) {
    req.BirthDt = person.dob ?? '';
  }
  if ((person.gender ?? '') !== (originalInq.gender ?? '')) {
    req.Gender = person.gender ?? '';
  }

  // Contact
  if ((person.phoneHome ?? '') !== (originalInq.phoneHome ?? '')) {
    req.PhoneHome = person.phoneHome ?? '';
  }
  if ((person.phoneCell ?? '') !== (originalInq.phoneCell ?? '')) {
    req.PhoneCell = person.phoneCell ?? '';
  }
  if ((person.phoneBus ?? '') !== (originalInq.phoneBus ?? '')) {
    req.PhoneBus = person.phoneBus ?? '';
  }
  if ((person.email ?? '') !== (originalInq.email ?? '')) {
    req.Email = person.email ?? '';
  }
  if (person.email2 !== undefined) {
    // ParsedCustInq doesn't track email2 — any non-undefined working value
    // is by definition a change.
    req.Email2 = person.email2;
  }

  // Address (physical)
  if ((person.addr ?? '') !== (originalInq.addr ?? '')) {
    req.StreetAddr1 = person.addr ?? '';
  }
  if ((person.city ?? '') !== (originalInq.city ?? '')) {
    req.City = person.city ?? '';
  }
  if ((person.state ?? '') !== (originalInq.state ?? '')) {
    req.State = person.state ?? '';
  }
  if ((person.zip ?? '') !== (originalInq.zip ?? '')) {
    req.Zip = person.zip ?? '';
  }

  // Mailing (ParsedCustInq doesn't carry mailing values — any working value
  // is emitted as-is when present).
  if (person.mailAddr !== undefined) req.MailAddr = person.mailAddr;
  if (person.mailCity !== undefined) req.MailCity = person.mailCity;
  if (person.mailState !== undefined) req.MailState = person.mailState;
  if (person.mailZip !== undefined) req.MailZip = person.mailZip;

  // Field 21 Employee Code (Bill 2026-10-17: the edit used to be dropped). Sent only when Core Director's current value
  // was read (x_BusDetail) and the officer changed it, so an unread value is never overwritten.
  if (originalInq.insiderCode && person.insiderCode !== undefined && (person.insiderCode ?? '') !== originalInq.insiderCode) {
    req.InsiderCode = person.insiderCode ?? '';
  }

  // Employment
  if ((person.employer ?? '') !== (originalInq.employer ?? '')) {
    req.EmplName = person.employer ?? '';
  }
  if ((person.occupation ?? '') !== (originalInq.occupation ?? '')) {
    req.OccType = person.occupation ?? '';
  }
  // EmploymentStatus intentionally NOT emitted (Bill 2026-07-22). It is a DERIVED
  // UI field with no Core Director destination — the backend CustModRequest has no
  // such parameter, so it was silently dropped, and emitting it here falsely counted
  // as a CIF modification. Employer/Occupation (real F141/F313) carry the actual
  // change above. See docs/operations/cif-write-integrity-gate-audit-2026-07-22.html.

  // Identification (CIP) — extras.idType/idNumber/idIssuer/idIssueDate/idExpiryDate
  const extra = (person.extra ?? {}) as Record<string, unknown>;
  const xIdType = typeof extra['idType'] === 'string' ? (extra['idType'] as string) : '';
  const xIdNumber = typeof extra['idNumber'] === 'string' ? (extra['idNumber'] as string) : '';
  const xIdIssuer = typeof extra['idIssuer'] === 'string' ? (extra['idIssuer'] as string) : '';
  const xIdIssueDate = typeof extra['idIssueDate'] === 'string' ? (extra['idIssueDate'] as string) : '';
  const xIdExpiryDate = typeof extra['idExpiryDate'] === 'string' ? (extra['idExpiryDate'] as string) : '';

  if (xIdType !== (originalInq.idTypeCode ?? '')) req.IdTypeCode = xIdType;
  if (xIdNumber !== (originalInq.idNumber ?? '')) req.IdNumber = xIdNumber;
  if (xIdIssuer !== (originalInq.idState ?? '')) req.IdState = xIdIssuer;
  if (xIdIssueDate !== (originalInq.idIssueDate ?? '')) req.IdIssueDate = xIdIssueDate;
  if (xIdExpiryDate !== (originalInq.idExpiryDate ?? '')) req.IdExpiryDate = xIdExpiryDate;

  // Compliance / CDD — ParsedCustInq doesn't surface these; emit when present.
  if (person.naicsCode !== undefined && person.naicsCode !== (originalInq.naicsCode ?? '')) {
    req.NaicsCode = person.naicsCode;
  }
  if (person.countryOfCitizenship !== undefined && person.countryOfCitizenship !== '') {
    req.CitizenshipCountry = person.countryOfCitizenship;
  }

  return req;
}

// -----------------------------------------------------------------------------
// Convenience — did anything actually change?
// -----------------------------------------------------------------------------

/**
 * Returns the set of CustModRequest keys that were populated by the diff
 * logic (i.e. everything beyond the mandatory `CifId / AuthMethod / OffCode`
 * envelope). Callers use this to decide whether to bail early.
 */
export function changedCustModKeys(req: CustModRequest): ReadonlyArray<keyof CustModRequest> {
  const mandatory = new Set<string>(['CifId', 'AuthMethod', 'OffCode']);
  return (Object.keys(req) as ReadonlyArray<keyof CustModRequest>).filter(
    (k) => !mandatory.has(k as string),
  );
}

/** Field keys whose change should trigger a CIP re-verify (IdVerifyAdd). */
export const ID_CUSTMOD_KEYS: ReadonlyArray<keyof CustModRequest> = [
  'IdTypeCode',
  'IdNumber',
  'IdState',
  'IdIssueDate',
  'IdExpiryDate',
];

/** Field keys that represent a contact change (drive ChangeOfContact chain). */
export const CONTACT_CUSTMOD_KEYS: ReadonlyArray<keyof CustModRequest> = [
  'StreetAddr1',
  'City',
  'State',
  'Zip',
  'MailAddr',
  'MailCity',
  'MailState',
  'MailZip',
  'PhoneHome',
  'PhoneCell',
  'PhoneBus',
  'PhoneOther',
  'Email',
  'Email2',
];
